import React from 'react';

const PralineSignaturContent = ({ product, sectionsRef }) => {
  return (
    <>
      {/* Hero Section */}
      <div
        ref={(el) => (sectionsRef.current[0] = el)}
        className="text-center mb-24"
      >
        <p className="text-sm tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--atelier-gold)' }}>
          SIGNATURE
        </p>
        <h1 className="text-6xl md:text-8xl font-light mb-8" style={{ color: 'var(--atelier-black)' }}>
          Uno Signatur Praliné
        </h1>
        <p className="text-xl md:text-2xl font-light italic max-w-3xl mx-auto" style={{ color: 'var(--atelier-grey)' }}>
          Gekarameliseerd. Ambachtelijk. Onweerstaanbaar.
        </p>
      </div>

      {/* Product Image */}
      <div
        ref={(el) => (sectionsRef.current[1] = el)}
        className="max-w-2xl mx-auto mb-24"
      >
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-auto"
        />
      </div>

      {/* Het Verhaal */}
      <section
        ref={(el) => (sectionsRef.current[2] = el)}
        className="max-w-4xl mx-auto mb-24"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
        <h2 className="text-4xl md:text-5xl font-light mb-8" style={{ color: 'var(--atelier-black)' }}>
          Het Verhaal
        </h2>
        <div className="space-y-6 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
          <p>
            In 1636 ontdekte een Franse kok per ongeluk iets magisch.
          </p>
          <p>
            Amandelen vielen in gesmolten suiker. In plaats van ze weg te gooien, proefde hij. 
            Wat hij proefde veranderde de patisserie voorgoed: praliné was geboren.
          </p>
          <p className="text-xl font-light" style={{ color: 'var(--atelier-black)' }}>
            Bijna vier eeuwen later eren wij die ontdekking.
          </p>
          <p>
            Uno Signatur Praliné is geen chocoladepasta met een vleugje noot. Dit is échte praliné — 
            gekarameliseerde hazelnoten en amandelen, vermalen tot een fluweelzachte crème. De techniek van 
            Franse maîtres chocolatiers, de ingrediënten van de beste Europese bronnen, de passie van Penotti Atelier.
          </p>
          <p className="text-xl font-light italic" style={{ color: 'var(--atelier-gold)' }}>
            Een smaak die sinds de 17e eeuw onweerstaanbaar is.
          </p>
        </div>
      </section>

      {/* De Oorsprong */}
      <section
        ref={(el) => (sectionsRef.current[3] = el)}
        className="max-w-4xl mx-auto mb-24"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
        <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
          De Oorsprong
        </h2>

        <div>
          <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
            De Kunst van Praliné
          </h3>
          <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
            <p>
              Praliné is simpel in concept, onmogelijk in perfectie.
            </p>
            <p>
              Neem noten. Karameliseer ze in suiker. Maal ze fijn. Klaar.
            </p>
            <p>
              Maar de details maken het verschil tussen middelmatig en meesterlijk. De temperatuur van de karamel. 
              Het moment waarop de noten erin gaan. De snelheid van het roeren. De duur van het malen. 
              Elke variabele verandert het eindresultaat.
            </p>
            <p>
              Wij hebben 47 proefbatches gemaakt voordat we tevreden waren.
            </p>
            <p className="text-2xl font-light" style={{ color: 'var(--atelier-gold)' }}>
              Batch 48 is wat je proeft.
            </p>
          </div>
        </div>
      </section>

      {/* De Ingrediënten */}
      <section
        ref={(el) => (sectionsRef.current[4] = el)}
        className="max-w-4xl mx-auto mb-24"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
        <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
          De Ingrediënten
        </h2>

        <div className="space-y-12">
          {/* Hazelnoten uit Piemonte */}
          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Hazelnoten uit Piemonte
            </h3>
            <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              <p>
                De basis. Dezelfde <em>Tonda Gentile</em> die we gebruiken in Uno Signatur — 's werelds meest gewilde hazelnoot. 
                Rond, compact, met een natuurlijke zoetheid die perfect karameliseert.
              </p>
              <p className="text-2xl font-light" style={{ color: 'var(--atelier-gold)' }}>
                60% van onze notenmix.
              </p>
            </div>
          </div>

          {/* Amandelen uit Valencia */}
          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Amandelen uit Valencia
            </h3>
            <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              <p>
                De Spaanse Marcona-amandel is de koningin van haar soort. Platter en ronder dan de Californische variant, 
                met een hogere oliegehalte en een zachtere, meer verfijnde smaak.
              </p>
              <p>
                Valencia's mediterrane klimaat — hete zomers, milde winters — produceert amandelen met een complexiteit 
                die je nergens anders vindt. Hints van honing. Een vleugje bitterheid die diepte geeft.
              </p>
              <p className="text-2xl font-light" style={{ color: 'var(--atelier-gold)' }}>
                40% van onze notenmix.
              </p>
            </div>
          </div>

          {/* Karamel */}
          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Karamel
            </h3>
            <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              <p>
                Geen kant-en-klare karamelsaus. Wij maken onze karamel vers, voor elke batch.
              </p>
              <p>
                Franse rietsuiker wordt langzaam verhit tot exact 175°C — het punt waarop de suikermoleculen transformeren 
                en die diepe, amberkleurige kleur ontwikkelen. Té vroeg gestopt en de karamel blijft plat. 
                Té laat en bitterheid overheerst.
              </p>
              <p className="text-2xl font-light" style={{ color: 'var(--atelier-gold)' }}>
                12 minuten. Handmatig geroerd. Elke batch.
              </p>
            </div>
          </div>

          {/* Zeezout */}
          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Zeezout
            </h3>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              Een snufje Fleur de Sel uit Guérande, Frankrijk. Het versterkt de karameltonen en balanceert de zoetheid. 
              Onzichtbaar, maar essentieel.
            </p>
          </div>
        </div>
      </section>

      {/* Het Proces */}
      <section
        ref={(el) => (sectionsRef.current[5] = el)}
        className="max-w-4xl mx-auto mb-24"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
        <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
          Het Proces
        </h2>

        <div className="space-y-12">
          {/* Stap 1: Roosteren */}
          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Stap 1: Roosteren
            </h3>
            <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              <p>
                De noten worden apart geroosterd — hazelnoten en amandelen hebben verschillende optimale temperaturen.
              </p>
              <div className="bg-white/50 p-6 rounded">
                <p className="mb-2">Hazelnoten: 150°C, 18 minuten.</p>
                <p>Amandelen: 160°C, 14 minuten.</p>
              </div>
              <p>
                Dit zorgt ervoor dat beide noten hun ideale smaakprofiel bereiken zonder dat de één op de ander moet wachten.
              </p>
            </div>
          </div>

          {/* Stap 2: Karameliseren */}
          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Stap 2: Karameliseren
            </h3>
            <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              <p>
                De versgemalen karamel wordt uitgegoten op een marmeren plaat — de traditionele methode. Het marmer koelt 
                de karamel snel en gelijkmatig, wat voorkomt dat de suiker blijft doorkoken.
              </p>
              <p>
                Op het perfecte moment — wanneer de karamel nog vloeibaar maar niet meer heet genoeg is om de noten te 
                verbranden — worden de geroosterde noten erdoor gehaald.
              </p>
              <p className="text-2xl font-light" style={{ color: 'var(--atelier-gold)' }}>
                Handwerk. Geen machines.
              </p>
            </div>
          </div>

          {/* Stap 3: Afkoelen */}
          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Stap 3: Afkoelen
            </h3>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              De gekarameliseerde noten worden uitgespreid om af te koelen. Dit duurt minimaal twee uur. 
              Geduld is niet optioneel.
            </p>
          </div>

          {/* Stap 4: Malen */}
          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Stap 4: Malen
            </h3>
            <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              <p>
                De afgekoelde praliné wordt in een granieten molen fijngemalen. De wrijving genereert warmte, waardoor de 
                natuurlijke oliën vrijkomen en de korrelige massa transformeert in een vloeibare crème.
              </p>
              <p className="text-2xl font-light" style={{ color: 'var(--atelier-gold)' }}>
                48 uur malen. Tot de perfecte textuur.
              </p>
              <p>
                Het resultaat: praliné die zo glad is dat je geen enkel stukje noot meer voelt, maar die nog steeds vol zit 
                met de gekarameliseerde smaak.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Proefnotities */}
      <section
        ref={(el) => (sectionsRef.current[6] = el)}
        className="max-w-4xl mx-auto mb-24 bg-white/50 p-12 rounded"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
        <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
          Proefnotities
        </h2>

        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--atelier-gold)' }}>
              Aroma
            </h4>
            <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
              Bij het openen — onmiddellijk de warme geur van gebrande suiker en geroosterde noten. Diep. Uitnodigend. Verslavend.
            </p>
          </div>
          <div>
            <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--atelier-gold)' }}>
              Eerste hap
            </h4>
            <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
              De karamel komt eerst. Zoet maar niet overheersend, met een lichte bitterheid die complexiteit biedt.
            </p>
          </div>
          <div>
            <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--atelier-gold)' }}>
              Ontwikkeling
            </h4>
            <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
              Dan de noten. De hazelnoot is rond en vertrouwd. De amandel voegt een subtiele frisheid toe die de zwaarte opheft.
            </p>
          </div>
          <div>
            <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--atelier-gold)' }}>
              Textuur
            </h4>
            <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
              Fluweelzacht met een lichte korrel — net genoeg om je eraan te herinneren dat dit échte noten zijn, geen pasta uit een fabriek.
            </p>
          </div>
          <div>
            <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--atelier-gold)' }}>
              Afdronk
            </h4>
            <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
              Lang en warm. De karamel blijft, de noten echo'en na. Een vleugje zout aan het einde.
            </p>
          </div>
        </div>
      </section>

      {/* De Praliné Traditie */}
      <section
        ref={(el) => (sectionsRef.current[7] = el)}
        className="max-w-4xl mx-auto mb-24"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
        <h2 className="text-4xl md:text-5xl font-light mb-8" style={{ color: 'var(--atelier-black)' }}>
          De Praliné Traditie
        </h2>
        <div className="space-y-6 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
          <p>
            Praliné is de basis van de Belgische en Franse chocoladekunst.
          </p>
          <p>
            Elke praline die je ooit hebt gegeten — van de duurste Belgische bonbon tot de eenvoudigste truffel — bevat praliné. 
            Het is het geheim ingrediënt dat chocolatiers al eeuwen koesteren.
          </p>
          <p>
            Maar praliné als <em>spread</em> is zeldzaam. Te arbeidsintensief. Te duur. Te complex voor massaproductie.
          </p>
          <p className="text-xl font-light" style={{ color: 'var(--atelier-black)' }}>
            Wij maken het toch.
          </p>
          <p className="text-xl font-light italic" style={{ color: 'var(--atelier-gold)' }}>
            Niet omdat het makkelijk is. Omdat het de moeite waard is.
          </p>
        </div>
      </section>

      {/* Momenten */}
      <section
        ref={(el) => (sectionsRef.current[8] = el)}
        className="max-w-4xl mx-auto mb-24"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
        <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
          Momenten
        </h2>

        <div className="space-y-8">
          <p className="text-xl leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
            Uno Signatur Praliné is veelzijdig. Verleidelijk op elk moment.
          </p>

          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Het Klassieke Moment
            </h3>
            <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              <p>
                Vers brioche. Nog warm. Een royale laag Praliné die smelt zodra ze het brood raakt. De geur die opstijgt 
                terwijl karamel en boter elkaar ontmoeten.
              </p>
              <p className="text-xl font-light italic" style={{ color: 'var(--atelier-gold)' }}>
                Dit is waarvoor het gemaakt is.
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Voor de Avonturiers
            </h3>
            <ul className="space-y-2 text-lg" style={{ color: 'var(--atelier-grey)' }}>
              <li>• Als vulling voor zelfgemaakte croissants</li>
              <li>• Door vanille-ijs als instant praline-ijs</li>
              <li>• Als basis voor een praliné-taart (voeg enkel boter en eieren toe)</li>
              <li>• Op een appel — de frisheid contrasteert hemels</li>
              <li>• Puur, met een espresso — de Italiaanse manier</li>
            </ul>
          </div>

          <div>
            <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
              Het Geheime Recept
            </h3>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
              Verwarm een eetlepel Praliné in een pannetje. Voeg een scheutje room toe. Roer tot glad. 
              Giet over vers fruit of ijs.
            </p>
            <p className="text-lg font-light italic mt-4" style={{ color: 'var(--atelier-gold)' }}>
              De eenvoudigste praliné-saus die je ooit zult maken.
            </p>
          </div>
        </div>
      </section>

      {/* De Drie Noten */}
      <section
        ref={(el) => (sectionsRef.current[9] = el)}
        className="max-w-4xl mx-auto mb-24"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
        <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
          De Drie Noten
        </h2>

        <p className="text-lg leading-relaxed mb-8" style={{ color: 'var(--atelier-grey)' }}>
          Penotti Atelier biedt drie Uno-varianten. Elk met een eigen karakter.
        </p>

        <div className="bg-white/50 p-8 rounded space-y-6">
          <div className="border-b pb-4" style={{ borderColor: 'var(--atelier-gold-light)' }}>
            <h4 className="text-xl font-light mb-2" style={{ color: 'var(--atelier-black)' }}>Uno Signatur</h4>
            <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>Intens. Puur. Hazelnoot & pure chocolade.</p>
          </div>
          <div className="border-b pb-4" style={{ borderColor: 'var(--atelier-gold-light)' }}>
            <h4 className="text-xl font-light mb-2" style={{ color: 'var(--atelier-black)' }}>Uno Signatur Blanc</h4>
            <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>Elegant. Subtiel. Witte chocolade & vanille.</p>
          </div>
          <div className="pb-4">
            <h4 className="text-xl font-light mb-2" style={{ color: 'var(--atelier-gold)' }}>Uno Signatur Praliné</h4>
            <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>Warm. Gekarameliseerd. Hazelnoot & amandel.</p>
          </div>
        </div>

        <p className="text-xl font-light mt-8" style={{ color: 'var(--atelier-grey)' }}>
          Praliné staat apart. Geen chocolade die domineert. Alleen de pure smaak van gekarameliseerde noten.
        </p>
        <p className="text-xl font-light italic" style={{ color: 'var(--atelier-black)' }}>
          Voor wie de noot belangrijker vindt dan de cacao.
        </p>
      </section>

      {/* Specificaties */}
      <section
        ref={(el) => (sectionsRef.current[10] = el)}
        className="max-w-4xl mx-auto mb-24"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
        <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
          Specificaties
        </h2>

        <div className="bg-white/50 p-8 rounded">
          <table className="w-full">
            <tbody className="space-y-4">
              {[
                ['Inhoud', '300g'],
                ['Hazelnoten', '60%, Piemonte Tonda Gentile'],
                ['Amandelen', '40%, Valencia Marcona'],
                ['Karameltemperatuur', '175°C'],
                ['Maaltijd', '48 uur stone-ground'],
                ['Zout', 'Fleur de Sel, Guérande'],
                ['Productie', 'Roosendaal, Nederland'],
                ['Bewaaradvies', 'Op kamertemperatuur, na openen binnen 8 weken'],
              ].map(([key, value]) => (
                <tr key={key} className="border-b border-gray-200">
                  <td className="py-4 text-lg font-light" style={{ color: 'var(--atelier-black)' }}>
                    {key}
                  </td>
                  <td className="py-4 text-lg text-right" style={{ color: 'var(--atelier-grey)' }}>
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Het Geheim van de Praliné */}
      <section
        ref={(el) => (sectionsRef.current[11] = el)}
        className="max-w-4xl mx-auto mb-24 text-center"
      >
        <div className="w-24 h-px bg-[var(--atelier-gold)] mx-auto mb-12" />
        <div className="space-y-6 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
          <p>
            Er is een reden waarom de beste chocolatiers ter wereld hun praliné-recept bewaken als een staatsgeheim.
          </p>
          <p className="text-xl font-light" style={{ color: 'var(--atelier-black)' }}>
            Het is de ziel van hun creaties. De basis waarop alles rust.
          </p>
          <p className="text-xl">
            Wij geven je die basis. In een pot. Klaar om te gebruiken.
          </p>
          <p className="text-2xl font-light italic mt-8" style={{ color: 'var(--atelier-gold)' }}>
            Wat je ermee doet, is aan jou.
          </p>
        </div>

        <div className="mt-16">
          <p className="text-xl font-light italic mb-6" style={{ color: 'var(--atelier-grey)' }}>
            "Vier eeuwen geleden viel een amandel in gesmolten suiker. De wereld is er nooit meer hetzelfde uit gaan zien."
          </p>
          <p className="text-2xl font-light" style={{ color: 'var(--atelier-black)' }}>
            Uno Signatur Praliné. Gekarameliseerde Perfectie.
          </p>
        </div>
      </section>
    </>
  );
};

export default PralineSignaturContent;
